/**
 * @module usePettyCash
 * Shared state for the petty cash / internal accounting module.
 *
 * Manages a double-entry bookkeeping system with:
 * - Journal entries (cash in/out transactions)
 * - Chart of accounts (expense, income, asset types)
 * - Suppliers and employees (linked to entries)
 * - Monthly periods that can be closed to prevent edits
 * - Account ledger with multi-filter drill-down
 * - Employee advance balances
 *
 * All state lives at module scope so the daybook, ledger, and balance views
 * share the same data without re-fetching. Active view selection (`activeView`)
 * controls which sub-page is rendered within the petty cash layout.
 */

import { ref, computed, watch } from "vue";
import { accountingApi } from "@/utils/api.js";

// ── Core data ─────────────────────────────────────────────────────────────────

const entries = ref([]);
const accounts = ref([]);
const suppliers = ref([]);
const employees = ref([]);
const locations = ref([]);  // pettycash_locations (managed via dots menu)
const periods = ref([]);
const currentPeriod = ref(null);
const options = ref({ locations: [], brands: [] });
const employeeBalances = ref([]);
const ledgerLines = ref([]);
const accountBalances = ref([]);

// VAT rate used for VAT-inclusive entry calculations (configurable via /api/accounting/pettycash/config)
const vatPercentage = ref(5);

// ── UI state ─────────────────────────────────────────────────────────────────

const loading = ref(false);
const error = ref(null);
const snackbar = ref(false);
const snackbarText = ref("");
const snackbarColor = ref("success");

const activeView = ref("daybook"); // "daybook" | "ledger" | "journal" | "balances" | "employees"

// ── Entry dialog ──────────────────────────────────────────────────────────────

const entryDialogOpen = ref(false);
const entryDialogMode = ref("create"); // "create" | "edit" | "duplicate" | "view"
const selectedEntry = ref(null);

// ── Daybook filters ───────────────────────────────────────────────────────────

const filterDialogOpen = ref(false);
const selectedMonth = ref(new Date().toISOString().substring(0, 7)); // YYYY-MM
const filterType = ref(null);       // "cash_out" | "cash_in"
const filterLocationId = ref(null);  // integer ID referencing sales DB locations
const filterBrandId = ref(null);     // integer ID referencing sales DB brands
const filterSupplier = ref(null);
const filterEmployee = ref(null);
const filterAccount = ref(null);
const filterSearch = ref(null);

// ── Employee balances filter ─────────────────────────────────────────────────
const employeesFilterDialogOpen = ref(false);
const employeeFilter = ref([]); // array of employee contact IDs

// ── Computed ────────────────────────────────────────────────────────────────

const filteredEmployeeBalances = computed(() => {
  if (!employeeFilter.value.length) return employeeBalances.value;
  return employeeBalances.value.filter((emp) =>
    employeeFilter.value.includes(emp.id),
  );
});

// ── Journal state (classic double-entry view) ────────────────────────────────

const journalLines = ref([]);

// Journal filters (same structure as ledger)
const journalFilterDialogOpen = ref(false);
const journalAccountId = ref([]);
const journalMonth = ref(null);
const journalLocationIds = ref([]);
const journalBrandIds = ref([]);
const journalEmployeeId = ref(null);
const journalSupplierId = ref(null);

const PETTYCASH_FILTERS_KEY = "pettyCashFilters";

// Restore filters from localStorage on init
const savedPcFilters = localStorage.getItem(PETTYCASH_FILTERS_KEY);
if (savedPcFilters) {
  try {
    const p = JSON.parse(savedPcFilters);
    // Daybook
    selectedMonth.value = p.selectedMonth ?? new Date().toISOString().substring(0, 7);
    filterType.value = p.filterType ?? null;
    filterLocationId.value = p.filterLocationId ?? null;
    filterBrandId.value = p.filterBrandId ?? null;
    filterSupplier.value = p.filterSupplier ?? null;
    filterEmployee.value = p.filterEmployee ?? null;
    filterAccount.value = p.filterAccount ?? null;
    filterSearch.value = p.filterSearch ?? null;
    // Journal
    journalAccountId.value = p.journalAccountId ?? [];
    journalMonth.value = p.journalMonth ?? null;
    journalLocationIds.value = p.journalLocationIds ?? [];
    journalBrandIds.value = p.journalBrandIds ?? [];
    journalEmployeeId.value = p.journalEmployeeId ?? null;
    journalSupplierId.value = p.journalSupplierId ?? null;
    // Employee balances
    employeeFilter.value = p.employeeFilter ?? [];
  } catch {}
}

// Auto-persist all filter state to localStorage on change (excludes ledger refs — persisted separately below after refs are declared)
watch(
  [selectedMonth, filterType, filterLocationId, filterBrandId, filterSupplier, filterEmployee, filterAccount, filterSearch,
    journalAccountId, journalMonth, journalLocationIds, journalBrandIds, journalEmployeeId, journalSupplierId, employeeFilter],
  () => {
    localStorage.setItem(
      PETTYCASH_FILTERS_KEY,
      JSON.stringify({
        selectedMonth: selectedMonth.value,
        filterType: filterType.value,
        filterLocationId: filterLocationId.value,
        filterBrandId: filterBrandId.value,
        filterSupplier: filterSupplier.value,
        filterEmployee: filterEmployee.value,
        filterAccount: filterAccount.value,
        filterSearch: filterSearch.value,
        journalAccountId: journalAccountId.value,
        journalMonth: journalMonth.value,
        journalLocationIds: journalLocationIds.value,
        journalBrandIds: journalBrandIds.value,
        journalEmployeeId: journalEmployeeId.value,
        journalSupplierId: journalSupplierId.value,
        employeeFilter: employeeFilter.value,
        ledgerAccountId: ledgerAccountId.value,
        ledgerMonth: ledgerMonth.value,
        ledgerLocationIds: ledgerLocationIds.value,
        ledgerBrandIds: ledgerBrandIds.value,
      }),
    );
  },
  { deep: true },
);

// ── Clear filters ─────────────────────────────────────────────────────────────

function clearFilters() {
  selectedMonth.value = new Date().toISOString().substring(0, 7);
  filterType.value = null;
  filterLocationId.value = null;
  filterBrandId.value = null;
  filterSupplier.value = null;
  filterEmployee.value = null;
  filterAccount.value = null;
  filterSearch.value = null;
  journalAccountId.value = [];
  journalMonth.value = null;
  journalLocationIds.value = [];
  journalBrandIds.value = [];
  journalEmployeeId.value = null;
  journalSupplierId.value = null;
  ledgerAccountId.value = [];
  ledgerMonth.value = new Date().toISOString().substring(0, 7);
  ledgerLocationIds.value = [];
  ledgerBrandIds.value = [];
  localStorage.removeItem(PETTYCASH_FILTERS_KEY);
}

function clearJournalFilters() {
  journalAccountId.value = [];
  journalMonth.value = null;
  journalLocationIds.value = [];
  journalBrandIds.value = [];
  journalEmployeeId.value = null;
  journalSupplierId.value = null;
  // Re-persist current state (without journal filters) to localStorage
  localStorage.setItem(
    PETTYCASH_FILTERS_KEY,
    JSON.stringify({
      selectedMonth: selectedMonth.value,
      filterType: filterType.value,
      filterLocationId: filterLocationId.value,
      filterBrandId: filterBrandId.value,
      filterSupplier: filterSupplier.value,
      filterEmployee: filterEmployee.value,
      filterAccount: filterAccount.value,
      filterSearch: filterSearch.value,
      journalAccountId: [],
      journalMonth: null,
      journalLocationIds: [],
      journalBrandIds: [],
      journalEmployeeId: null,
      journalSupplierId: null,
      ledgerAccountId: ledgerAccountId.value,
      ledgerMonth: ledgerMonth.value,
      ledgerLocationIds: ledgerLocationIds.value,
      ledgerBrandIds: ledgerBrandIds.value,
    }),
  );
}

// ── Ledger state ──────────────────────────────────────────────────────────────

const ledgerFilterDialogOpen = ref(false);
const ledgerAccountId = ref([]);
const ledgerMonth = ref(new Date().toISOString().substring(0, 7));
const ledgerLocationIds = ref([]);  // integer IDs for ledger location filter
const ledgerBrandIds = ref([]);     // integer IDs for ledger brand filter
const ledgerEmployeeId = ref(null);
const ledgerSupplierId = ref(null);

// Restore ledger filters from localStorage (after ledger refs are declared above)
const savedLedgerFilters = localStorage.getItem(PETTYCASH_FILTERS_KEY);
if (savedLedgerFilters) {
  try {
    const p = JSON.parse(savedLedgerFilters);
    ledgerAccountId.value = p.ledgerAccountId ?? [];
    ledgerMonth.value = p.ledgerMonth ?? new Date().toISOString().substring(0, 7);
    ledgerLocationIds.value = p.ledgerLocationIds ?? [];
    ledgerBrandIds.value = p.ledgerBrandIds ?? [];
  } catch {}
}

// Auto-persist ledger filter state when any ledger ref changes
watch(
  [ledgerAccountId, ledgerMonth, ledgerLocationIds, ledgerBrandIds],
  () => {
    // Read current persisted state and update only the ledger fields
    const saved = localStorage.getItem(PETTYCASH_FILTERS_KEY);
    const current = saved ? JSON.parse(saved) : {};
    localStorage.setItem(
      PETTYCASH_FILTERS_KEY,
      JSON.stringify({
        ...current,
        ledgerAccountId: ledgerAccountId.value,
        ledgerMonth: ledgerMonth.value,
        ledgerLocationIds: ledgerLocationIds.value,
        ledgerBrandIds: ledgerBrandIds.value,
      }),
    );
  },
  { deep: true },
);

function clearLedgerFilters() {
  ledgerAccountId.value = [];
  ledgerMonth.value = new Date().toISOString().substring(0, 7);
  ledgerLocationIds.value = [];
  ledgerBrandIds.value = [];
  // Re-persist current state (without ledger filters) to localStorage
  localStorage.setItem(
    PETTYCASH_FILTERS_KEY,
    JSON.stringify({
      selectedMonth: selectedMonth.value,
      filterType: filterType.value,
      filterLocationId: filterLocationId.value,
      filterBrandId: filterBrandId.value,
      filterSupplier: filterSupplier.value,
      filterEmployee: filterEmployee.value,
      filterAccount: filterAccount.value,
      filterSearch: filterSearch.value,
      journalAccountId: journalAccountId.value,
      journalMonth: journalMonth.value,
      journalLocationIds: journalLocationIds.value,
      journalBrandIds: journalBrandIds.value,
      journalEmployeeId: journalEmployeeId.value,
      journalSupplierId: journalSupplierId.value,
      ledgerAccountId: [],
      ledgerMonth: new Date().toISOString().substring(0, 7),
      ledgerLocationIds: [],
      ledgerBrandIds: [],
    }),
  );
}

// ── Account type computeds ────────────────────────────────────────────────────

// Account dropdowns in the entry dialog are filtered by type so the user
// can't accidentally debit an income account or credit an expense account.
const expenseAccounts = computed(() => accounts.value.filter((a) => a.type === "expense"));
const incomeAccounts = computed(() => accounts.value.filter((a) => a.type === "income"));

// Cash-out: money leaves the petty cash box — we debit (reduce) an expense or asset account
// while the credit side is always CASH. CASH itself is the credit account, never the target
// (debit) side, so it is excluded from the dropdown to prevent invalid entries.
const cashOutAccounts = computed(() => accounts.value.filter((a) => (a.type === "expense" || a.type === "asset") && a.code !== "CASH"));
// Cash-in: money enters the petty cash box — we credit (increase) an income or asset account
// while the debit side is always CASH. CASH is always the debit account, never the target.
const cashInAccounts = computed(() => accounts.value.filter((a) => (a.type === "income" || a.type === "asset") && a.code !== "CASH"));
// Adjustment: no cash moves — the entry debits a target account and credits
// creditAccountId (mandatory, must differ). Both CASH and OPENING_BAL are excluded
// from the debit side; only CASH is excluded from the credit side.
const adjustmentAccounts = computed(() => accounts.value.filter((a) => a.code !== "CASH" && a.code !== "OPENING_BAL"));
// Credit-account side: OPENING_BAL is valid here (e.g. formalizing a prior-period balance).
const creditAccountOptions = computed(() => accounts.value.filter((a) => a.code !== "CASH"));

// ── Snackbar helper ───────────────────────────────────────────────────────────

function showSnackbar(text, color = "success") {
  snackbarText.value = text;
  snackbarColor.value = color;
  snackbar.value = true;
}

// ── API calls ─────────────────────────────────────────────────────────────────

async function fetchAccounts() {
  try {
    const { data } = await accountingApi.get("/api/accounting/pettycash/accounts");
    accounts.value = data;
  } catch (err) {
    console.error("Failed to fetch accounts:", err);
  }
}

async function fetchSuppliers() {
  try {
    const { data } = await accountingApi.get("/api/accounting/pettycash/suppliers");
    suppliers.value = data;
  } catch (err) {
    console.error("Failed to fetch suppliers:", err);
  }
}

async function fetchEmployees() {
  try {
    const { data } = await accountingApi.get("/api/accounting/pettycash/employees");
    employees.value = data;
  } catch (err) {
    console.error("Failed to fetch employees:", err);
  }
}

// Fetch all active locations (both local and sales-synced)
async function fetchLocations() {
  try {
    const { data } = await accountingApi.get("/api/accounting/pettycash/locations");
    locations.value = data;
  } catch (err) {
    console.error("Failed to fetch locations:", err);
  }
}

async function fetchConfig() {
  try {
    const { data } = await accountingApi.get("/api/accounting/pettycash/config");
    if (data.vat_percentage !== undefined) {
      vatPercentage.value = parseFloat(data.vat_percentage);
    }
  } catch (err) {
    console.error("Failed to fetch config:", err);
  }
}

async function fetchOptions() {
  try {
    const { data } = await accountingApi.get("/api/accounting/pettycash/options");
    options.value = data;
  } catch (err) {
    console.error("Failed to fetch options:", err);
  }
}

/** Fetch journal entries for the selected month, applying all active filters. */
async function fetchEntries() {
  loading.value = true;
  try {
    const params = { month: selectedMonth.value };
    if (filterAccount.value) params.account_id = filterAccount.value;
    if (filterType.value) params.type = filterType.value;
    // Location/brand filters use integer IDs referencing the sales DB
    if (filterLocationId.value) params.location_id = filterLocationId.value;
    if (filterBrandId.value) params.brand_id = filterBrandId.value;
    if (filterSupplier.value) params.supplier_id = filterSupplier.value;
    if (filterEmployee.value) params.employee_id = filterEmployee.value;
    if (filterSearch.value) params.search = filterSearch.value;
    const { data } = await accountingApi.get("/api/accounting/pettycash/entries", { params });
    entries.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("Failed to fetch entries:", err);
    error.value = err.response?.data?.error || err.message;
  } finally {
    loading.value = false;
  }
}

async function fetchPeriodStatus() {
  try {
    const { data } = await accountingApi.get(`/api/accounting/pettycash/periods/${selectedMonth.value}`);
    currentPeriod.value = data;
  } catch (err) {
    console.error("Failed to fetch period:", err);
    currentPeriod.value = null;
  }
}

// Each mutating operation re-fetches entries and period status so the UI
// stays in sync without manual cache invalidation.

async function createEntry(entryData) {
  const { data } = await accountingApi.post("/api/accounting/pettycash/entries", entryData);
  await fetchEntries();
  await fetchPeriodStatus();
  showSnackbar("Entry created");
  return data;
}

async function updateEntry(id, entryData) {
  const { data } = await accountingApi.put(`/api/accounting/pettycash/entries/${id}`, entryData);
  await fetchEntries();
  await fetchPeriodStatus();
  showSnackbar("Entry updated");
  return data;
}

async function deleteEntryById(id) {
  await accountingApi.delete(`/api/accounting/pettycash/entries/${id}`);
  await fetchEntries();
  await fetchPeriodStatus();
  showSnackbar("Entry deleted");
}

// ── Period management ─────────────────────────────────────────────────────────

async function closePeriodAction() {
  try {
    await accountingApi.post(`/api/accounting/pettycash/periods/${selectedMonth.value}/close`);
    await fetchPeriodStatus();
    showSnackbar(`Month ${selectedMonth.value} closed`);
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to close month", "error");
  }
}

async function reopenPeriodAction() {
  try {
    await accountingApi.post(`/api/accounting/pettycash/periods/${selectedMonth.value}/reopen`);
    await fetchPeriodStatus();
    showSnackbar(`Month ${selectedMonth.value} reopened`);
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to reopen month", "error");
  }
}

// ── Inline entity creation ────────────────────────────────────────────────────
// These allow the entry dialog to create suppliers/employees/accounts on the
// fly without leaving the form, improving data-entry flow.

async function createEmployeeInline(name) {
  const { data } = await accountingApi.post("/api/accounting/pettycash/employees", { name });
  employees.value.push(data);
  return data;
}

async function createAccountInline(code, name, type) {
  const { data } = await accountingApi.post("/api/accounting/pettycash/accounts", { code, name, type });
  accounts.value.push(data);
  return data;
}

async function updateAccountById(id, payload) {
  const { data } = await accountingApi.put(`/api/accounting/pettycash/accounts/${id}`, payload);
  const idx = accounts.value.findIndex((a) => a.id === id);
  if (idx >= 0) {
    if (data.is_active === false) {
      accounts.value.splice(idx, 1);
    } else {
      accounts.value[idx] = data;
    }
  }
  return data;
}

// Create a new petty-cash location (or reactivate if name was deactivated)
async function createLocationInline(name) {
  const { data } = await accountingApi.post("/api/accounting/pettycash/locations", { name });
  locations.value.push(data);
  return data;
}

// Update location name or deactivate (soft-delete)
async function updateLocationById(id, payload) {
  const { data } = await accountingApi.put(`/api/accounting/pettycash/locations/${id}`, payload);
  const idx = locations.value.findIndex((l) => l.id === id);
  if (idx >= 0) {
    if (data.is_active === false) {
      locations.value.splice(idx, 1);
    } else {
      locations.value[idx] = data;
    }
  }
  return data;
}

// ── Reporting ─────────────────────────────────────────────────────────────────

async function fetchEmployeeBalances() {
  try {
    const { data } = await accountingApi.get("/api/accounting/pettycash/employee-balances");
    employeeBalances.value = data;
  } catch (err) {
    console.error("Failed to fetch employee balances:", err);
  }
}

/** Fetch account ledger lines for the selected account(s) and optional filters. */
async function fetchLedger() {
  // Support both single value and array of account IDs
  const ids = Array.isArray(ledgerAccountId.value) ? ledgerAccountId.value : [ledgerAccountId.value].filter(Boolean);
  try {
    const params = {};
    // When accounts are selected, filter by them; otherwise fetch all
    if (ids.length) params.account_id = ids.join(",");
    if (ledgerMonth.value) params.month = ledgerMonth.value;
    // Ledger location/brand filters use integer IDs
    if (ledgerLocationIds.value.length) params.location_ids = ledgerLocationIds.value.join(",");
    if (ledgerBrandIds.value.length) params.brand_ids = ledgerBrandIds.value.join(",");
    if (ledgerEmployeeId.value) params.employee_id = ledgerEmployeeId.value;
    if (ledgerSupplierId.value) params.supplier_id = ledgerSupplierId.value;
    const { data } = await accountingApi.get("/api/accounting/pettycash/ledger", { params });
    ledgerLines.value = data;
  } catch (err) {
    console.error("Failed to fetch ledger:", err);
  }
}

async function fetchAccountBalances() {
  try {
    const params = {};
    if (selectedMonth.value) params.month = selectedMonth.value;
    const { data } = await accountingApi.get("/api/accounting/pettycash/balances", { params });
    accountBalances.value = data;
  } catch (err) {
    console.error("Failed to fetch account balances:", err);
  }
}

/** Fetch journal lines (classic double-entry view) for the selected month, applying all filters. */
async function fetchJournal() {
  try {
    const params = { month: selectedMonth.value };
    // Apply journal-specific filters (journalMonth can override selectedMonth for ledger-style range)
    if (journalAccountId.value?.length) params.account_id = journalAccountId.value.join(",");
    if (journalLocationIds.value?.length) params.location_ids = journalLocationIds.value.join(",");
    if (journalBrandIds.value?.length) params.brand_ids = journalBrandIds.value.join(",");
    if (journalEmployeeId.value) params.employee_id = journalEmployeeId.value;
    if (journalSupplierId.value) params.supplier_id = journalSupplierId.value;
    const { data } = await accountingApi.get("/api/accounting/pettycash/journal", { params });
    journalLines.value = data;
  } catch (err) {
    console.error("Failed to fetch journal:", err);
  }
}

// ── Dialog open helpers ───────────────────────────────────────────────────────

function openCreateDialog() {
  selectedEntry.value = null;
  entryDialogMode.value = "create";
  entryDialogOpen.value = true;
}

/** Open entry dialog in view mode (read-only) — fetch full entry with journal lines */
async function openViewDialog(entry) {
  loading.value = true;
  try {
    const { data } = await accountingApi.get(`/api/accounting/pettycash/entries/${entry.id}`);
    selectedEntry.value = data;
    entryDialogMode.value = "view";
    entryDialogOpen.value = true;
  } catch (err) {
    error.value = err.response?.data?.error || err.message;
  } finally {
    loading.value = false;
  }
}

/** Open edit dialog — fetch full entry details first to get journal lines (credit account) */
async function openEditDialog(entry) {
  loading.value = true;
  try {
    const { data } = await accountingApi.get(`/api/accounting/pettycash/entries/${entry.id}`);
    selectedEntry.value = data;
    entryDialogMode.value = "edit";
    entryDialogOpen.value = true;
  } catch (err) {
    error.value = err.response?.data?.error || err.message;
  } finally {
    loading.value = false;
  }
}

// Duplicate pre-fills the form with the existing entry's values so the user
// can quickly create a similar entry without re-entering every field
function openDuplicateDialog(entry) {
  selectedEntry.value = entry;
  entryDialogMode.value = "duplicate";
  entryDialogOpen.value = true;
}

// Reconstruct display description: [employee/supplier: ]<description>[ | brand][ @location]
function buildDisplayDescription(entry) {
  const who = entry.employee_name || entry.supplier_name || null;
  const desc = entry.description?.trim() || "";
  let result = "";
  // Who prefix — only add colon separator when desc follows
  if (who) result += desc ? `${who}: ` : who;
  result += desc;
  // Brand suffix before location
  if (entry.brand_name) result += result ? ` | ${entry.brand_name}` : entry.brand_name;
  // Location suffix
  if (entry.location_name) result += ` @${entry.location_name}`;
  return result;
}

// ── Composable export ─────────────────────────────────────────────────────────

/**
 * Singleton composable for petty cash state. All consumers share the same reactive
 * refs at module scope so the daybook, ledger, journal, and employee-balances views
 * stay in sync without redundant API calls.
 *
 * @returns {{ entries, accounts, suppliers, employees, locations, periods, currentPeriod,
 *   options, employeeBalances, ledgerLines, journalLines, accountBalances, vatPercentage,
 *   loading, error, snackbar, snackbarText, snackbarColor, activeView, daybookViewMode,
 *   employeesViewMode, journalFilterDialogOpen, journalAccountId, journalMonth, ... }}
 */
export function usePettyCash() {
  return {
    // State
    entries, accounts, suppliers, employees, locations, periods, currentPeriod,
    options, employeeBalances, ledgerLines, journalLines, accountBalances, vatPercentage,
    loading, error, snackbar, snackbarText, snackbarColor,

    // View & dialog
    activeView,
    entryDialogOpen, entryDialogMode, selectedEntry,

    // Daybook filters
    filterDialogOpen,
    selectedMonth, filterType, filterAccount, filterLocationId, filterBrandId,
    filterSupplier, filterEmployee, filterSearch,

    // Employee balances filter
    employeesFilterDialogOpen,
    employeeFilter,

    // Ledger filters
    ledgerFilterDialogOpen,
    ledgerAccountId, ledgerMonth, ledgerLocationIds, ledgerBrandIds, ledgerEmployeeId, ledgerSupplierId,

    // Journal filters
    journalFilterDialogOpen,
    journalAccountId, journalMonth, journalLocationIds, journalBrandIds, journalEmployeeId, journalSupplierId,

    // Computed
    expenseAccounts, incomeAccounts, cashOutAccounts, cashInAccounts, adjustmentAccounts, creditAccountOptions,
    filteredEmployeeBalances,

    // Methods
    fetchAccounts, fetchSuppliers, fetchEmployees, fetchLocations, fetchOptions, fetchConfig,
    fetchEntries, fetchPeriodStatus,
    createEntry, updateEntry, deleteEntryById,
    closePeriodAction, reopenPeriodAction,
    createEmployeeInline, createAccountInline, createLocationInline,
    updateAccountById, updateLocationById,
    fetchEmployeeBalances, fetchLedger, fetchJournal, fetchAccountBalances,
    openCreateDialog, openViewDialog, openEditDialog, openDuplicateDialog, showSnackbar,
    buildDisplayDescription, clearFilters, clearJournalFilters, clearLedgerFilters,
  };
}
